import { io, type Socket } from 'socket.io-client';
import type {
  RTCClientOptions,
  RTCNotificationEnvelope,
  SignalRequest,
  SignalResponse
} from '../types/index.js';
import { EventEmitter } from '../utils/EventEmitter.js';
import { toError } from '../utils/browser.js';

interface SocketEvents {
  connecting: undefined;
  connected: undefined;
  disconnected: string | undefined;
  reconnect_failed: undefined;
  error: Error;
  message: RTCNotificationEnvelope;
}

export class SocketClient {
  private readonly events = new EventEmitter<SocketEvents>();
  private readonly connectionTimeout: number;
  private readonly requestTimeout: number;
  private socket: Socket | null = null;

  constructor(private readonly options: RTCClientOptions) {
    if (!options.serverUrl?.trim()) throw new Error('serverUrl is required');
    if (!options.token?.trim()) throw new Error('token is required');
    this.connectionTimeout = options.connectionTimeout ?? 10_000;
    this.requestTimeout = options.requestTimeout ?? 15_000;
  }

  on<K extends keyof SocketEvents & string>(
    event: K,
    listener: (payload: SocketEvents[K]) => void
  ): () => void {
    return this.events.on(event, listener);
  }

  async connect(): Promise<void> {
    if (this.socket?.connected) return;
    if (!this.socket) this.socket = this.createSocket();
    const socket = this.socket;
    this.events.emit('connecting', undefined);

    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error(`RTC connection timed out after ${this.connectionTimeout}ms`));
      }, this.connectionTimeout);

      const onConnect = (): void => {
        cleanup();
        resolve();
      };
      const onError = (error: unknown): void => {
        cleanup();
        reject(toError(error, 'RTC connection failed'));
      };
      const cleanup = (): void => {
        clearTimeout(timer);
        socket.off('connect', onConnect);
        socket.off('connect_error', onError);
      };

      socket.once('connect', onConnect);
      socket.once('connect_error', onError);
      socket.connect();
    });
  }

  disconnect(): void {
    this.socket?.disconnect();
  }

  isConnected(): boolean {
    return Boolean(this.socket?.connected);
  }

  async request<T>(request: SignalRequest): Promise<T> {
    const socket = this.socket;
    if (!socket?.connected) throw new Error('RTC socket is not connected');

    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error(`RTC request timed out: ${request.action}`));
      }, this.requestTimeout);

      socket.emit('request', request, (response: SignalResponse<T>) => {
        clearTimeout(timer);
        if (!response?.success) {
          reject(new Error(response?.error || `RTC request failed: ${request.action}`));
          return;
        }
        resolve(response.data as T);
      });
    });
  }

  private createSocket(): Socket {
    const factory = this.options.socketIO ?? io;
    const socket = factory(this.options.serverUrl, {
      autoConnect: false,
      auth: { token: this.options.token },
      query: { token: this.options.token, appId: this.options.appId ?? '' },
      transports: this.options.transports ?? ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5
    });

    socket.on('connect', () => this.events.emit('connected', undefined));
    socket.on('disconnect', (reason) => this.events.emit('disconnected', reason));
    socket.on('connect_error', (error) => this.events.emit('error', toError(error)));
    socket.io.on('reconnect_failed', () => this.events.emit('reconnect_failed', undefined));
    socket.on('message', (message: RTCNotificationEnvelope) => {
      if (message && typeof message.event === 'string') this.events.emit('message', message);
    });
    return socket;
  }
}

